import { useEffect, useState } from 'react';
import { ExerciseStatus, IExerciseAlternative } from '../../../models/exercise';
import ExerciseAlternative from '../../molecules/ExerciseAlternative';

export type TAlternativeListType = 'normal' | 'result' | 'retry';

interface iAlternativesListProps {
  examId?: string;
  questionId?: string;
  alternatives: IExerciseAlternative[];
  spacing: 'small' | 'medium' | 'large';
  selectedAlternativeId?: string;
  type?: TAlternativeListType;
  showCorrectAnswer?: boolean;
  showResult?: boolean;
  onSelectAlternative?: (alternative: IExerciseAlternative) => void;
  defaultStrikeoutAlternativesIds?: string[];
}

function AlternativesList({
  spacing = 'medium',
  alternatives,
  selectedAlternativeId,
  type = 'normal',
  showCorrectAnswer = true,
  showResult = false,
  onSelectAlternative,
  defaultStrikeoutAlternativesIds,
}: iAlternativesListProps) {
  const [selectedAlternative, setSelectedAlternative] = useState<
    IExerciseAlternative | undefined
  >(() => {
    return alternatives.find(
      (alternative) => alternative.id === selectedAlternativeId
    );
  });

  const [strikeoutAlternativesIds, setStrikeoutAlternativesIds] = useState<
    string[]
  >(defaultStrikeoutAlternativesIds || []);

  function onStrikeoutAlternative(alternativeId: string) {
    if (strikeoutAlternativesIds.includes(alternativeId)) {
      setStrikeoutAlternativesIds((prev) =>
        prev.filter((id) => id !== alternativeId)
      );
    } else {
      setStrikeoutAlternativesIds((prev) => [...prev, alternativeId]);
    }
  }

  function getAlternativeStatus(
    alternative: IExerciseAlternative
  ): ExerciseStatus {
    const isAlternativeSelected = alternative.id === selectedAlternative?.id;
    const showAlternativeStatus =
      type === 'result' || (showResult && type === 'retry');

    if (
      isAlternativeSelected &&
      alternative.correct === true &&
      showAlternativeStatus
    ) {
      return 'success';
    }

    if (
      isAlternativeSelected &&
      alternative.correct === false &&
      showAlternativeStatus
    ) {
      return 'error';
    }

    if (
      !isAlternativeSelected &&
      alternative.correct === true &&
      showCorrectAnswer
    ) {
      return 'success';
    }

    if (isAlternativeSelected && alternative.correct === undefined) {
      return 'selected';
    }

    return 'default';
  }

  useEffect(() => {
    const alternative = alternatives.find(
      (alternative) => alternative.id === selectedAlternativeId
    );

    setSelectedAlternative(alternative);
  }, [selectedAlternativeId, alternatives]);

  useEffect(() => {
    if (defaultStrikeoutAlternativesIds) {
      setStrikeoutAlternativesIds(defaultStrikeoutAlternativesIds);
    }
  }, [defaultStrikeoutAlternativesIds]);

  return (
    <ul className={`flex flex-col ${spacing === 'small' ? 'gap-sm' : spacing === 'medium' ? 'gap-md' : 'gap-lg'}`}>
      {alternatives.map((alternative) => {
        if (!alternative) {
          return;
        }

        return (
          <ExerciseAlternative
            key={`exerciseAlternative${alternative.id}`}
            alternativeId={alternative.id}
            text={alternative.text || ''}
            value={alternative.text || ''}
            type={'single_answer'}
            status={getAlternativeStatus(alternative)}
            selected={
              selectedAlternative && selectedAlternative.id === alternative.id
            }
            isStrikeout={strikeoutAlternativesIds.includes(alternative.id)}
            onSelected={() => {
              if (selectedAlternative?.id === alternative.id) {
                return;
              }

              setSelectedAlternative(alternative);

              if (type === 'retry') {
                onSelectAlternative && onSelectAlternative(alternative);
                return;
              }

              setStrikeoutAlternativesIds((prev) =>
                prev.filter((id) => id !== alternative.id)
              );
            }}
            disabled={type === 'result' || (type === 'retry' && showResult)}
            onStrikeout={() => onStrikeoutAlternative(alternative.id)}
            isShowingCorrectAnswer={showCorrectAnswer}
          />
        );
      })}
    </ul>
  );
}

export { AlternativesList };
